import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/**
 * LegalPageLayout - Reusable layout for legal pages (Aviso Legal, Privacidad, Cookies)
 * Features breadcrumb navigation and elegant typography
 */
export const LegalPageLayout = ({ title, children, lastUpdated }) => {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Hero Header */}
      <div className="relative py-20 md:py-28 border-b border-white/5">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="container mx-auto px-4 relative">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/50 mb-8">
            <Link to="/" className="hover:text-[var(--primary)] transition-colors flex items-center gap-1">
              <Home size={14} />
              <span>Inicio</span>
            </Link>
            <ChevronRight size={14} />
            <span className="text-white">{title}</span>
          </nav>
          
          {/* Title */}
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-white mb-4">
            {title}
          </h1>
          
          {/* Gold underline */}
          <div className="w-24 h-1 bg-[var(--primary)]" />
          
          {lastUpdated && (
            <p className="text-white/40 text-sm mt-6">
              Última actualización: {lastUpdated}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-invert prose-lg max-w-none legal-content">
            {children}
          </div>
        </div>
      </div>

      {/* Back to top */}
      <div className="container mx-auto px-4 pb-12">
        <div className="max-w-3xl mx-auto flex justify-center">
          <Link 
            to="/" 
            className="text-[var(--primary)] hover:text-white transition-colors text-sm uppercase tracking-widest flex items-center gap-2"
          >
            ← Volver al inicio
          </Link>
        </div>
      </div>

      {/* Styles for legal content */}
      <style>{`
        .legal-content h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.75rem;
          color: white;
          margin-top: 3rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid rgba(212, 175, 55, 0.3);
        }
        .legal-content h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.25rem;
          color: white;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .legal-content p {
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.8;
          margin-bottom: 1rem;
        }
        .legal-content ul, .legal-content ol {
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        .legal-content li {
          margin-bottom: 0.5rem;
        }
        .legal-content strong {
          color: white;
          font-weight: 600;
        }
        .legal-content a {
          color: #D4AF37;
          text-decoration: none;
        }
        .legal-content a:hover {
          text-decoration: underline;
        }
        .legal-content .highlight-box {
          background: rgba(212, 175, 55, 0.1);
          border-left: 3px solid #D4AF37;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
        }
        .legal-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        .legal-content th, .legal-content td {
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 0.75rem 1rem;
          text-align: left;
        }
        .legal-content th {
          background: rgba(255, 255, 255, 0.05);
          color: white;
          font-weight: 600;
        }
        .legal-content td {
          color: rgba(255, 255, 255, 0.7);
        }
      `}</style>
    </div>
  );
};

export default LegalPageLayout;
